<script lang="ts" setup>
import { useAccount } from "@/composables/account/useAccount";
import { useI18n } from "vue-i18n";
import BaseFeedback from "@/components/base/BaseFeedback.vue";
import AddonsDetails from "@/components/addons/AddonsDetails.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { useRouter } from "vue-router";
import BaseButton from "@/components/base/BaseButton.vue";
import TariffMain from "@/components/tariff/TariffMain.vue";
import AddonsMain from "@/components/addons/AddonsMain.vue";
import BaseModal from "@/components/base/BaseModal.vue";

const router = useRouter();
const { t } = useI18n();

const {
  addonOptions,
  chosenAddon,
  closeFeedbackResultModal,
  discountAmount,
  discountName,
  disableAddon,
  enabledOptions,
  errorModal,
  feedbackModal,
  feedbackResult,
  feedbackResultModal,
  handleFeedbackSubmitResult,
  isAddonDetails,
  isLoading,
  nextPaymentDate,
  openAddonDetails,
  promoModal,
  promoModalAction,
  removeModal,
  servicesMenu,
  successModal,
  tariffOptions,
  tariffPrice,
  tariffPriceDiscount,
  toPayment,
  toggleModal,
  walletAmountCalc,
  configDataBrand,
} = useAccount();
</script>

<template>
  <div v-if="!isAddonDetails" class="account">
    <div class="grid-block account-balance">
      <div class="account-balance__title">
        <p class="caption-medium--bold">{{ t("account.balance.title") }}</p>
      </div>
      <div class="account-balance__value">
        <p class="h1">{{ walletAmountCalc }}₽</p>
      </div>
      <div class="account-balance__btn">
        <BaseButton
          :title="t('account.balance.topUp')"
          size="medium"
          @click="toPayment"
        />
      </div>
    </div>
    <div class="grid-block account-tariff">
      <TariffMain
        :options="tariffOptions"
        :nextDate="nextPaymentDate"
        :amount="tariffPriceDiscount"
        :old-amount="tariffPrice"
        :discount="discountAmount"
        :discount-name="discountName"
      />
    </div>
    <router-link to="/history" class="grid-block account-history">
      <BaseFigureForIcon
        class="account-history--pic"
        icon="transactions"
        color="grey-secondary"
        size="large"
      />
      <div class="account-history--title">
        <p class="h4 color-text-primary">{{ t("account.history.title") }}</p>
      </div>
      <div class="account-history--link">
        <BaseFigureForIcon
          icon="more"
          color="grey-secondary"
          size="small"
          is-button
        />
      </div>
    </router-link>
    <div v-if="enabledOptions" class="grid-block account-bottom">
      <AddonsMain :options="addonOptions" @action="openAddonDetails" />
    </div>
    <div class="grid-block account-services">
      <div class="account-services__title">
        <p class="h3">{{ t("account.section.services") }}</p>
      </div>
      <div class="account-services__body">
        <div
          v-for="(item, index) in servicesMenu"
          :key="index"
          class="account-services__body-item"
          @click="router.push(item.link)"
        >
          <BaseFigureForIcon
            :icon="item.icon"
            size="extra-large"
            color="secondary"
          />
          <div class="account-services__body-item_title">
            <p class="h4 color-text-primary">{{ item.title }}</p>
          </div>
        </div>
      </div>
    </div>
    <template v-if="isLoading">
      <div class="loader">
        <div class="loader-pic">
          <img :src="configDataBrand.assets.loader" alt="Загрузка" />
        </div>
      </div>
    </template>
  </div>
  <div v-if="isAddonDetails" class="details">
    <div class="details-header">
      <div class="details-header--back" @click="isAddonDetails = false">
        <BaseFigureForIcon
          size="medium"
          color="white"
          icon="arrow"
          position="left"
          is-button
        />
      </div>
    </div>
    <AddonsDetails
      :chosen-addon="chosenAddon"
      :is-active="true"
      @action="toggleModal(true, 'remove')"
    />
  </div>
  <Teleport to=".root">
    <BaseModal
      v-if="removeModal"
      :title="t('account.modals.removeTitle')"
      :main-btn-title="t('common.actions.no')"
      :secondary-btn-title="t('common.actions.yes')"
      @close="toggleModal(false, 'remove')"
      @action="toggleModal(false, 'remove')"
      @secondary-action="disableAddon"
    />
    <BaseModal
      v-if="errorModal"
      :title="t('common.errors.title')"
      :main-btn-title="t('account.modals.errorClose')"
      @close="toggleModal(false, 'error')"
      @action="toggleModal(false, 'error')"
    />
    <BaseModal
      v-if="successModal"
      :title="t('account.modals.successTitle')"
      :main-btn-title="t('common.actions.ok')"
      @close="toggleModal(false, 'success')"
      @action="toggleModal(false, 'success')"
    />
    <BaseModal
      v-if="promoModal"
      :title="t('account.modals.promoTitle')"
      :main-btn-title="t('account.modals.promoAction')"
      @close="toggleModal(false, 'promo')"
      @action="promoModalAction"
    />
    <BaseModal
      v-if="feedbackResultModal"
      :title="
        feedbackResult
          ? t('account.modals.feedbackThanksTitle')
          : t('common.errors.title')
      "
      :text="
        feedbackResult
          ? t('account.modals.feedbackThanksText')
          : t('account.modals.feedbackErrorText')
      "
      :main-btn-title="
        feedbackResult
          ? t('account.modals.feedbackClose')
          : t('account.modals.feedbackRetry')
      "
      :is-success-modal="feedbackResult"
      @close="closeFeedbackResultModal"
      @action="closeFeedbackResultModal"
    />
    <BaseFeedback
      v-model="feedbackModal"
      @submit-result="handleFeedbackSubmitResult"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@import "@/assets/style/components/account";
</style>
